import apiClient from './apiClient';

export const workspaceApi = {
  /** Curated templates from ``backend/data/workspace_templates`` (JSON exports). */
  getWorkspaceTemplates: async () => {
    const response = await apiClient.get('/workspaces/templates');
    return response.data;
  },

  getWorkspaceTemplateDetail: async (filename) => {
    const encoded = encodeURIComponent(filename);
    const response = await apiClient.get(`/workspaces/templates/${encoded}`, {
      suppressGlobalErrorToast: true,
    });
    return response.data;
  },

  /** Create a workspace for the current user from a bundled template file. */
  instantiateWorkspaceTemplate: async (filename) => {
    const encoded = encodeURIComponent(filename);
    const response = await apiClient.post(
      `/workspaces/templates/${encoded}/instantiate`,
      {},
      { suppressGlobalErrorToast: true }
    );
    const ws = response.data;
    return {
      ...ws,
      id: ws.id.toString(),
      sources: ws.tables,
      chatsCount: ws.chat_count,
      lastAccessed: 'Just now'
    };
  },

  getWorkspaces: async () => {
    const response = await apiClient.get('/workspaces');
    return response.data.map(ws => ({
      ...ws,
      id: ws.id.toString(), // Ensure ID is string for frontend consistency
      sources: ws.tables,
      chatsCount: ws.chat_count
    }));
  },

  getWorkspaceById: async (id) => {
    const response = await apiClient.get(`/workspaces/${id}`);
    return {
      ...response.data,
      id: response.data.id.toString(),
      sources: response.data.tables,
      chatsCount: response.data.chat_count
    };
  },

  createWorkspace: async (data) => {
    // data should contain { name, description, table_ids }
    const response = await apiClient.post('/workspaces', {
      name: data.name,
      description: data.description,
      table_ids: data.sources || [], // sources array from frontend wizard contains table IDs
      udf_ids: data.udf_ids || []
    });

    const ws = response.data;
    return {
      ...ws,
      id: ws.id.toString(),
      sources: ws.tables,
      chatsCount: ws.chat_count,
      lastAccessed: 'Just now'
    };
  },

  deleteWorkspace: async (id) => {
    const response = await apiClient.delete(`/workspaces/${id}`);
    return response.data;
  },

  updateWorkspace: async (id, data, requestOptions = {}) => {
    // data should contain { name, description, sources, udf_ids }
    const payload = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.description !== undefined) payload.description = data.description;
    if (data.sources !== undefined) payload.table_ids = data.sources;
    if (data.udf_ids !== undefined) payload.udf_ids = data.udf_ids;

    const response = await apiClient.patch(`/workspaces/${id}`, payload, {
      suppressGlobalErrorToast: requestOptions.suppressGlobalErrorToast,
    });

    const ws = response.data;
    return {
      ...ws,
      id: ws.id.toString(),
      sources: ws.tables,
      chatsCount: ws.chat_count
    };
  },

  getWorkspaceHistoryCounts: async (workspaceId) => {
    const response = await apiClient.get(`/workspaces/${workspaceId}/history-counts`);
    return response.data;
  },

  getWorkspaceArtefacts: async (workspaceId) => {
    const response = await apiClient.get(`/workspaces/${workspaceId}/artefacts`);
    return response.data;
  },

  hasMapArtefacts: async () => {
    const response = await apiClient.get('/workspaces/meta/has-map-artefacts');
    return Boolean(response.data?.has_map_artefacts);
  },

  getWorkspaceMapLayers: async (workspaceId) => {
    const response = await apiClient.get(`/workspaces/${workspaceId}/map-layers`);
    return response.data;
  },

  saveArtefact: async (workspaceId, data, requestOptions = {}) => {
    const response = await apiClient.post(
      `/workspaces/${workspaceId}/artefacts`,
      data,
      { suppressGlobalErrorToast: requestOptions.suppressGlobalErrorToast }
    );
    return response.data;
  },

  updateArtefact: async (id, data, requestOptions = {}) => {
    const response = await apiClient.patch(`/workspaces/artefacts/${id}`, data, {
      suppressGlobalErrorToast: requestOptions.suppressGlobalErrorToast,
    });
    return response.data;
  },

  deleteArtefact: async (id) => {
    const response = await apiClient.delete(`/workspaces/artefacts/${id}`);
    return response.data;
  },

  /** Editor only: creates workspace from export JSON; upserts datasets / UDFs for current user. */
  importWorkspace: async (document) => {
    const response = await apiClient.post('/workspaces/import', document, {
      suppressGlobalErrorToast: true,
    });
    const ws = response.data;
    return {
      ...ws,
      id: ws.id.toString(),
      sources: ws.tables,
      chatsCount: ws.chat_count,
      lastAccessed: 'Just now'
    };
  },

  /** Editor only: downloads workspace + dataset metadata as JSON. */
  downloadWorkspaceExport: async (id) => {
    const response = await apiClient.get(`/workspaces/${id}/export`, {
      responseType: 'blob',
      suppressGlobalErrorToast: true,
    });
    const blob = response.data;
    const disposition = response.headers['content-disposition'];
    let filename = `workspace-${id}.json`;
    const match = disposition && disposition.match(/filename="([^"]+)"/);
    if (match) filename = match[1];
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  getEmbed: async (id) => {
    const response = await apiClient.get(`/workspaces/${id}/embed`, {
      suppressGlobalErrorToast: true,
    });
    return response.data;
  },

  publishEmbed: async (id, data) => {
    const response = await apiClient.post(`/workspaces/${id}/embed`, data || {});
    return response.data;
  },

  updateEmbed: async (id, data) => {
    const response = await apiClient.patch(`/workspaces/${id}/embed`, data);
    return response.data;
  },

  revokeEmbed: async (id) => {
    await apiClient.delete(`/workspaces/${id}/embed`);
  },
};
